import React, { useState, useMemo } from 'react';
import { Gauge } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBMI, bmiPrime, getBMIPrimeCategory, getBMICategory } from '../../lib/calculatorUtils';

const BMIPrimeCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!h || !w || h < 50 || w < 20) return null;
    const bmi = calculateBMI(w, h);
    const bp = bmiPrime(bmi);
    return { bmi, bp, category: getBMIPrimeCategory(bp), bmiCat: getBMICategory(bmi) };
  }, [heightCm, weightKg]);

  const inputs = (
    <div className="space-y-5" data-testid="bp-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Enter Your Details</h2>
      <p className="text-slate-400 text-sm mb-5">BMI Prime expresses BMI as a ratio to the upper normal limit (25).</p>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="BMI Prime" value={result.bp} accent="from-blue-500 to-violet-500" category={result.category} testId="bp-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="BMI" value={result.bmi} testId="bp-bmi" />
        <StatRow label="BMI category" value={result.bmiCat.label} color={result.bmiCat.color} testId="bp-bmi-category" />
        <StatRow label="BMI Prime category" value={result.category.label} color={result.category.color} testId="bp-category" />
        <StatRow label="Underweight" value="< 0.74" />
        <StatRow label="Normal" value="0.74 – 1.00" />
        <StatRow label="Overweight" value="1.00 – 1.20" />
        <StatRow label="Obese" value="> 1.20" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        BMI Prime &gt; 1.00 means you're above the upper normal BMI limit by that fraction (e.g. 1.10 = 10% above).
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter height and weight to compute BMI Prime." />
  );

  return (
    <CalculatorLayout
      slug="bmi-prime" title="BMI Prime Calculator"
      subtitle="A more intuitive variation of BMI that expresses your BMI as a ratio to the upper normal limit (25)."
      metaDescription="BMI Prime Calculator, a ratio variation of BMI for clearer obesity classification."
      icon={Gauge}
      accentGradient="from-blue-500 to-violet-500" accentColor="text-blue-400"
      badgeBg="bg-blue-500/10" badgeBorder="border-blue-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Formula:</strong> BMI Prime = BMI ÷ 25</p>
        <p>A value of 1.0 corresponds to the upper end of healthy BMI.</p>
      </>}
    />
  );
};

export default BMIPrimeCalculatorPage;
